import React, { useState } from 'react';
import { Calculator, Building2, DollarSign, Ban as Bank, FileText, Briefcase, PiggyBank, Scale, ArrowRight } from 'lucide-react';

interface FinancingOption {
  title: string;
  description: string;
  requirements: string[];
  terms: string[];
  icon: React.ReactNode;
  interestRate: string;
  maxAmount: string;
}

const FinancingOptionsPage: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const financingOptions: FinancingOption[] = [
    {
      title: "Traditional Commercial Mortgage",
      description: "Long-term financing for commercial property purchases with competitive rates and extended repayment terms.",
      requirements: [
        "Credit score 680+",
        "20-30% down payment",
        "2+ years business history",
        "Detailed business plan",
        "Financial statements"
      ],
      terms: [
        "15-30 year terms",
        "Fixed or variable rates",
        "Monthly payments",
        "Prepayment options available"
      ],
      icon: <Building2 className="h-6 w-6 text-primary" />,
      interestRate: "5.5% - 7.0%",
      maxAmount: "$5,000,000"
    },
    {
      title: "SBA 504 Loan",
      description: "Government-backed financing with lower down payments and competitive rates for owner-occupied commercial real estate.",
      requirements: [
        "Credit score 650+",
        "10% down payment",
        "Owner-occupied property",
        "Net worth under $15M",
        "US-based business"
      ],
      terms: [
        "10-25 year terms",
        "Fixed rate structure",
        "CDC portion up to 40%",
        "Bank portion up to 50%"
      ],
      icon: <Bank className="h-6 w-6 text-primary" />,
      interestRate: "3.0% - 5.0%",
      maxAmount: "$5,500,000"
    },
    {
      title: "Bridge Loan",
      description: "Short-term financing to bridge the gap between property purchase and long-term financing arrangement.",
      requirements: [
        "Credit score 650+",
        "25%+ down payment",
        "Exit strategy required",
        "Property valuation",
        "Quick closing possible"
      ],
      terms: [
        "6-24 month terms",
        "Interest-only payments",
        "Flexible prepayment",
        "Quick funding"
      ],
      icon: <Briefcase className="h-6 w-6 text-primary" />,
      interestRate: "7.0% - 12.0%",
      maxAmount: "$3,000,000"
    },
    {
      title: "Equipment Financing",
      description: "Specific financing for business equipment and fixtures with the equipment serving as collateral.",
      requirements: [
        "Credit score 600+",
        "Equipment specs required",
        "Business plan",
        "1+ year in business",
        "Equipment as collateral"
      ],
      terms: [
        "2-7 year terms",
        "Fixed monthly payments",
        "Equipment ownership option",
        "Tax advantages"
      ],
      icon: <PiggyBank className="h-6 w-6 text-primary" />,
      interestRate: "6.0% - 9.0%",
      maxAmount: "$2,000,000"
    }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-primary">Financing Options</h1>
        <p className="text-xl text-muted-foreground">
          Explore available financing solutions for your business location
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {financingOptions.map((option) => (
          <div
            key={option.title}
            className={`bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6 cursor-pointer transition-colors ${
              selectedOption === option.title ? 'border-primary' : 'hover:border-primary/50'
            }`}
            onClick={() => setSelectedOption(option.title)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                {option.icon}
                <h2 className="text-xl font-semibold ml-2">{option.title}</h2>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Interest Rate</p>
                <p className="font-semibold">{option.interestRate}</p>
              </div>
            </div>
            
            <p className="mt-4 text-muted-foreground">{option.description}</p>
            
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <p className="font-medium mb-2">Max Amount</p>
                <p className="text-xl font-semibold text-primary">{option.maxAmount}</p>
              </div>
              <div className="text-right">
                <button className="bg-primary/10 text-primary px-4 py-2 rounded-lg hover:bg-primary/20 transition-colors">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedOption && (
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">
            {financingOptions.find(opt => opt.title === selectedOption)?.title} Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-4 flex items-center">
                <FileText className="mr-2 text-primary" />
                Requirements
              </h3>
              <ul className="space-y-2">
                {financingOptions
                  .find(opt => opt.title === selectedOption)
                  ?.requirements.map((req, index) => (
                    <li key={index} className="flex items-center">
                      <ArrowRight className="mr-2 h-4 w-4 text-primary" />
                      {req}
                    </li>
                  ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4 flex items-center">
                <Scale className="mr-2 text-primary" />
                Terms
              </h3>
              <ul className="space-y-2">
                {financingOptions
                  .find(opt => opt.title === selectedOption)
                  ?.terms.map((term, index) => (
                    <li key={index} className="flex items-center">
                      <ArrowRight className="mr-2 h-4 w-4 text-primary" />
                      {term}
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 p-4 bg-primary/10 rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center">
              <Calculator className="mr-2 text-primary" />
              Next Steps
            </h3>
            <p className="text-muted-foreground">
              Ready to explore this financing option? Connect with our financial experts for a detailed consultation and personalized rate quote.
            </p>
            <button className="mt-4 bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors">
              Schedule Consultation
            </button>
          </div>
        </div>
      )}

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-6">Financial Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <h3 className="font-medium">Documentation</h3>
            <p className="text-sm text-muted-foreground">
              Access guides and checklists for loan applications and financial planning.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Calculator Tools</h3>
            <p className="text-sm text-muted-foreground">
              Use our financial calculators to estimate payments and analyze costs.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Expert Support</h3>
            <p className="text-sm text-muted-foreground">
              Get personalized assistance from our financial advisors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancingOptionsPage;